import { useEffect, useRef, useState } from "react";

import { Stack, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { ClientAppEvent } from "constants/index";
import {
  useDisclosure,
  useQuoteViewTracker,
  useShipperShipmentPageContext,
} from "hooks";
import { useQuoteQAAnswerNotifications } from "hooks/useQuoteQAAnswerNotifications";
import {
  hasCompetitivenessVerdict,
  isCompetitivenessSort,
  shipmentIsBooked,
  shipperFilterQuotes,
  shipperSortQuotes,
  shipperSortQuotesByColumn,
  sortQuotesByCompetitiveness,
} from "utils";

import {
  QuoteFilterDropdown,
  QuoteHistoryModal,
  QuoteSortDropdown,
  ShipmentQuoteTable,
} from "../ShipmentQuoteTable";
import { getDefaultFilter } from "./utils";
import { QuoteQAModal } from "../ShipmentQuoteTable/components/QuoteQAModal";

import type { QuoteViewType } from "../ShipmentQuoteTable";

interface Props {
  shipment: ShipperCondensedShipment;
}

export function ShipmentQuoteList(props: Props) {
  const { shipment } = props;
  const posthog = usePostHog();
  const { scoreByQuoteId, scoresError } = useShipperShipmentPageContext();

  // Filter and sort state
  const [currentSort, setCurrentSort] = useState<QuoteSortType>("ALL");
  const [currentFilter, setCurrentFilter] = useState<QuoteFilterType>(
    getDefaultFilter(shipment),
  );

  // Table column sorting state
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Modal state using useDisclosure
  const qaModal = useDisclosure();
  const historyModal = useDisclosure();
  const [selectedQuote, setSelectedQuote] = useState<
    ShipperCondensedQuote | undefined
  >();

  const shipmentRef = useRef<undefined | ShipperCondensedShipment>();
  useEffect(() => {
    /*
      This hook keeps track of the shipment ID between renders to ensure
      that the filters and sort are reset when the shipment changes. The
      competitiveness sorts are not offered on every shipment, so a carried
      over one could name an option the next shipment does not have.
      */
    if (shipment && shipmentRef.current?.info?.id !== shipment.info.id) {
      shipmentRef.current = shipment;
      setCurrentFilter(getDefaultFilter(shipment));
      setCurrentSort("ALL");
    }
  }, [shipment]);

  // Process quotes: filter, sort, and remove hidden
  const preFilteredQuotes = shipment.condensed_quotes.filter(
    (quote: ShipperCondensedQuote) =>
      !quote.carrier_is_blocked && quote.hidden_at == null,
  );
  const filteredQuotes = shipperFilterQuotes(preFilteredQuotes, currentFilter);

  // Apply sorting - either table column sorting or dropdown sorting
  let processedQuotes: ShipperCondensedQuote[];
  if (sortColumn) {
    processedQuotes = shipperSortQuotesByColumn(
      filteredQuotes,
      sortColumn,
      sortDirection,
    );
  } else if (isCompetitivenessSort(currentSort)) {
    processedQuotes = sortQuotesByCompetitiveness(
      filteredQuotes,
      scoreByQuoteId,
      currentSort,
    );
  } else {
    processedQuotes = shipperSortQuotes(filteredQuotes, currentSort);
  }

  // Fetch notifications once for all quotes to avoid N+1 query problem
  const allQuoteIds = processedQuotes.map((quote) => quote.quote);

  const { notificationsByQuote } = useQuoteQAAnswerNotifications({
    quoteIds: allQuoteIds,
  });

  // Record quotes as viewed once their row has dwelled on-screen (see the row).
  const { markQuoteViewed } = useQuoteViewTracker({
    shipmentId: shipment.info.id,
  });

  // Handlers for table interactions
  const handleQuoteAction = (
    action: QuoteViewType,
    quote: ShipperCondensedQuote,
  ) => {
    setSelectedQuote(quote);

    if (action === "QA") {
      qaModal.onOpen();
    } else if (action === "HISTORY") {
      historyModal.onOpen();
    }
  };

  const handleSortChange = (column: string, direction: "asc" | "desc") => {
    setSortColumn(column);
    setSortDirection(direction);
    // Clear dropdown sort when using column sorting
    setCurrentSort("ALL");
  };

  const handleRecoveryAction = () => {
    setCurrentFilter("ALL");
  };

  if (preFilteredQuotes.length === 0) {
    return (
      <Typography variant="body1" textAlign="center">
        You have not received any quotes on this shipment.
      </Typography>
    );
  }

  return (
    <>
      <Stack direction="column" mt=".5rem">
        {/* Filter and Sort Controls */}
        <Stack direction="row" spacing={2} mb="1rem" alignItems="center">
          <QuoteFilterDropdown
            value={currentFilter ?? "ALL"}
            onChange={(value: QuoteFilterType) => setCurrentFilter(value)}
            shipmentBooked={shipmentIsBooked(shipment)}
            showCancelled={preFilteredQuotes.some(
              (quote: ShipperCondensedQuote) =>
                quote.internal_state === "CANCELLED",
            )}
            showCountered={preFilteredQuotes.some(
              (quote: ShipperCondensedQuote) =>
                quote.internal_state === "COUNTERED",
            )}
          />
          <QuoteSortDropdown
            value={currentSort}
            showCompetitivenessSort={hasCompetitivenessVerdict(scoreByQuoteId)}
            onChange={(value: QuoteSortType) => {
              if (isCompetitivenessSort(value)) {
                posthog?.capture(
                  ClientAppEvent.SHIPPER_QUOTE_COMPETITIVENESS_SCORE_SORT_APPLIED,
                  { shipment_id: shipment.info.id, sort: value },
                );
              }
              setCurrentSort(value);
              // Clear table column sorting when using dropdown sort
              setSortColumn("");
            }}
          />
          <Typography color="text.secondary" variant="body1">
            {`${processedQuotes.length} out of ${preFilteredQuotes.length} quote${preFilteredQuotes.length === 1 ? "" : "s"} matched filters`}
          </Typography>
        </Stack>
        {/* Scores are an extra on the table, so a failure is reported without
        holding up the quotes themselves. */}
        <ErrorTypography error={scoresError} />
        {/* Quotes Table */}
        <ShipmentQuoteTable
          quotes={processedQuotes}
          shipment={shipment}
          onQuoteAction={handleQuoteAction}
          onSortChange={handleSortChange}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onRecoveryAction={handleRecoveryAction}
          notificationsByQuote={notificationsByQuote}
          onQuoteViewed={markQuoteViewed}
        />
      </Stack>

      {/* Modals */}
      <QuoteQAModal
        open={qaModal.isOpen}
        onClose={qaModal.onClose}
        quote={selectedQuote}
      />
      <QuoteHistoryModal
        open={historyModal.isOpen}
        onClose={historyModal.onClose}
        quote={selectedQuote}
      />
    </>
  );
}
